import { Typography } from '@material-ui/core';
import React, { FC, memo, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { withTheme } from '@/se/theme';
import responsive from '@/se/utilities/responsive';
import { formatTimeEntered } from '../entities/common/transducers';
import { MONITOR_STATUS_ICONS, STATUSES, PROCEDURE_STATUSES } from '../entities/patient/enums';
import { OR_TIMES_S } from '../inputs/timeEdit/TimeEditModal';
import StaffShifts from '../pages/kiosk/schedule/staff/StaffShifts';
import TurnoverClock, { isSameDate } from '../pages/kiosk/tablet/TurnoverClock';
import {
  eventTime,
  getLogEntries,
  isHelpFn,
  isFlipRoomFn,
  patientEnteredTime,
  isOrTimeActive,
  optionalFormat,
  showValueIf,
} from '../pages/kiosk/tablet/utils';
import { AirFiltrationIndicatorTimer } from '../pages/kiosk/widgets/AirFiltrationIndicator';
import PatientInfo from '../patient/PatientSlot/PatientInfo';

import PanelContainer from './PanelContainer';
import { Room } from '@/components/pages/kiosk/monitor';
import { mockStaff } from '@/components/pages/kiosk/monitor/staff.mock';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';

interface StaffMemberData {
  type: 'staff' | 'physician' | 'anesthesiologist';
  text: string;
  id: string;
  entityId: string;
  name: string;
  title: string;
  fullTitle: string;
  entryTime?: string;
  arrivedOnSite: string | null;
  timeOfDay: string;
}

interface PanelProps {
  operatingRoom: Room;
  compactView: boolean;
  allowWrap: boolean;
  miniView: boolean;
  numberOfRooms: number;
  preOpFocused?: boolean;
  pacuFocused?: boolean;
  isPreOp?: boolean;
  isSuperAdmin?: boolean;
  isGroupAdmin?: boolean;
  messagingView?: boolean;
  staffMode?: boolean;
  showScheduledProcedureDuration?: boolean;
  roomStaff: StaffMemberData[];
  hasOtherStatuses?: boolean;
  isKiosk?: boolean;
}

interface VisualisationProps {
  compactView: boolean;
  $humanCompact?: boolean;
}

interface IconProps {
  compactView: boolean;
  miniView: boolean;
  $humanCompact?: boolean;
}

interface FooterProps {
  compactView: boolean;
}

interface PatientInfoWrapperProps {
  preOpFocused?: boolean;
  pacuFocused?: boolean;
  miniView: boolean;
  messagingView?: boolean;
  isPatientReady?: boolean;
}

interface ClockWrapperProps {
  miniView: boolean;
}

interface TitleWithIndicatorProps {
  roomId: string;
  title: string;
  compactView: boolean;
}

interface TurnoverClockProps {
  room: Room;
  status: string;
  warningThreshold?: number;
  alertThreshold?: number;
  miniView: boolean;
  humanCompact?: boolean;
}

export const Visualisation = styled.div<VisualisationProps>`
  display: flex;
  justify-content: center;
  padding: 0 2em;
  margin-top: ${props => (props.compactView ? '0' : 'auto')};
  flex: ${props => (props.compactView ? '1' : '0 1 100%')};
  align-items: center;

  ${props =>
    props.$humanCompact &&
    css`
      position: absolute;
      top: 0.25rem;
      right: 0.25rem;
      padding: 0.25rem;
      margin: 0;
      flex: none;
      width: 3rem;
    `}

  ${responsive.sm.andSmaller`
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    padding: 0.25rem;
    margin: 0;
    flex: none;
    width: 3rem;
  `};
`;

export const Icon = styled.img<IconProps>`
  max-width: 100%;
  height: calc(min(6vh, 16vw));

  ${props =>
    props.compactView &&
    !props.$humanCompact &&
    css`
      max-width: 35%;
      height: calc(min(4.2vh, 11vw));
    `}

  ${props =>
    props.$humanCompact &&
    css`
      height: auto;
      width: 100%;
      max-width: 100%;
    `}

  ${responsive.sm.andSmaller`
    height: auto;
    width: 100%;
    max-width: 100%;
  `};

  ${props =>
    props.miniView &&
    !props.$humanCompact &&
    css`
      max-width: initial;
      width: auto;
      height: 3vh;

      @media (max-height: 1000px) {
        height: 1vh;
      }
    `}
`;

// CSS animation replacements for react-pose
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Footer = styled.div<FooterProps>`
  margin-top: ${props => (props.compactView ? '0' : 'auto')};
  animation: ${fadeInUp} 3s ease forwards;
`;

const AnimatedVisualisation = styled(Visualisation)`
  animation: ${fadeInUp} 3s ease forwards;
`;

const TimesCompact = styled.div`
  display: flex;
  flex-flow: column;
  color: ${withTheme(theme => theme.textColor.string())};
  white-space: nowrap;

  margin-top: 0.5rem;
  border-top: solid 2px alpha(${withTheme(theme => theme.textColor.string())}, 0.2);
  align-content: flex-start;
  font-size: 0.875em;

  ${responsive.sm.andSmaller`
    font-size: 0.75rem;
    margin-top: 0.5rem;
    gap: 0;
  `};
`;

const Entry = styled.div`
  display: flex;
  color: ${withTheme(theme => theme.textColor.string())};

  > * {
    flex: 1 0 50%;
  }
`;

const Label = styled.div`
  opacity: 0.75;
`;

const Time = styled.div`
  color: ${withTheme(theme => theme.textColor.string())};
  text-align: right;
`;

const PatientInfoWrapper = styled.div<PatientInfoWrapperProps>`
  padding: ${props =>
    props.preOpFocused || props.pacuFocused ? '0 1em' : props.messagingView ? '1.25vh' : '0.5vh 1em'};
  margin-left: -1rem;
  margin-right: -1rem;
  margin-bottom: -0.5rem;
  border-radius: 0.25rem;
  border: 2px solid ${props => (props.isPatientReady ? '#00B63E' : 'transparent')};
  background: ${props => (props.isPatientReady ? 'rgba(255,255,255,0.05)' : 'transparent')};

  ${props =>
    props.miniView &&
    css`
      padding: 0.5rem 1rem;
      border: none;
    `}
`;

const indicatorStyles = {
  margin: '0.5em',
  width: '4rem',
  height: '4rem',
  borderWidth: '2px',
};

const floatRight = {
  ...indicatorStyles,
  position: 'absolute' as const,
  right: 0,
};

const floatCenter = {
  ...indicatorStyles,
  position: 'absolute' as const,
  left: '50%',
  transform: 'translateX(-50%)',
  margin: '0 auto',
};

const StatusWrapper = styled.div`
  margin: auto 0;
`;

const ClockWrapper = styled(StatusWrapper)<ClockWrapperProps>`
  grid-column: 1 / span 2;
  margin: 0;
  justify-content: center;
  align-items: center;
  font-size: 0.25rem;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  padding: 1rem;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 0.2rem;
    padding: 0.5rem;
  }

  ${props =>
    props.miniView &&
    css`
      max-width: initial;
      width: auto;
      height: 3vh;

      @media (max-height: 1000px) {
        height: 1vh;
      }
    `}
`;

const TurnoverClockComponent = memo<TurnoverClockProps>(
  ({ room, status, warningThreshold, alertThreshold, miniView, humanCompact }) => (
    <ClockWrapper miniView={miniView}>
      <TurnoverClock
        room={room}
        status={status}
        warningThreshold={warningThreshold}
        alertThreshold={alertThreshold}
        humanCompact={humanCompact}
      />
    </ClockWrapper>
  )
);

TurnoverClockComponent.displayName = 'TurnoverClockComponent';

const TitleWithIndicator: FC<TitleWithIndicatorProps> = ({ roomId, title, compactView }) => (
  <div style={{ display: 'flex' }}>
    <label>{title}</label>
    <AirFiltrationIndicatorTimer roomId={roomId} style={compactView ? floatRight : floatCenter} radius={32} />
  </div>
);

const Panel: FC<PanelProps> = ({
  operatingRoom,
  compactView,
  allowWrap,
  miniView,
  numberOfRooms,
  isPreOp,
  isSuperAdmin,
  isGroupAdmin,
  messagingView,
  staffMode,
  roomStaff,
  hasOtherStatuses = false,
  isKiosk,
}) => {
  // Human compact: compact view on non-kiosk (laptop/desktop) screens — chevron top-right, tighter layout
  // Only engage for 7+ ORs; small orgs (≤6 ORs fit in one row) use kiosk-like full-size cards
  const humanCompact = numberOfRooms > 6 && !isKiosk;
  // Extract data from operatingRoom
  const {
    id: roomId,
    name: title,
    status,
    statusLabel,
    monitorStatus,
    procedure: scheduleProcedure,
    hideTurnoverClock,
    warningThreshold,
    alertThreshold,
  } = operatingRoom;

  const { preOpFocused, pacuFocused, showScheduledProcedureDuration } = useScreenConfiguration();

  const patient = operatingRoom?.patient;
  const procedure = operatingRoom?.patient?.procedureType?.name;
  const physician = operatingRoom?.patient?.physician?.name;
  const patientName = operatingRoom?.patient?.name;
  const patientId = operatingRoom?.patient?.id;
  const patientType = operatingRoom?.patient?.type;
  const patientBedNumber = operatingRoom.patients?.[0]?.bed;
  const enteredAtTime = useMemo(() => patientEnteredTime(patient, operatingRoom), [operatingRoom, patient]);
  const timeoutTime = useMemo(() => eventTime(PROCEDURE_STATUSES.TIME_OUT, patient), [patient]);
  const procedureStartTime = useMemo(() => eventTime(PROCEDURE_STATUSES.ONGOING, patient), [patient]);
  const closingTime = useMemo(() => eventTime(PROCEDURE_STATUSES.CLOSING, patient), [patient]);
  const called = operatingRoom?.patient?.called && hasOtherStatuses;
  const transferredToChair = operatingRoom?.patient?.transferredToChair && hasOtherStatuses;
  const transferredToPT = operatingRoom?.patient?.transferredToPT && hasOtherStatuses;
  const ptComplete = operatingRoom?.patient?.ptComplete && hasOtherStatuses;
  const numberOfPanels = useMemo(() => {
    const rows = Math.ceil(numberOfRooms / 8);
    return Math.ceil(numberOfRooms / rows);
  }, [numberOfRooms]);

  const { isHelp, isFlipRoom, flipRoomTimestamp } = useMemo(() => {
    const logEntries = getLogEntries(patient);
    const isHelp = isHelpFn(logEntries);
    const isFlipRoom = isFlipRoomFn(logEntries);
    const flipRoomTimestamp = [...logEntries].reverse().find(item => item.type === 'FlipRoom')?.timestamp;

    return { isHelp, isFlipRoom, flipRoomTimestamp };
  }, [patient]);

  const derivedScheduleProcedure = useMemo(() => {
    if (!enteredAtTime || !scheduleProcedure) {
      return scheduleProcedure;
    }
    const patient = scheduleProcedure.patient ?? {};
    const events = patient.events ?? {};

    if (events.orAt === enteredAtTime) {
      return scheduleProcedure;
    }

    return {
      ...scheduleProcedure,
      patient: {
        ...patient,
        events: {
          ...events,
          orAt: enteredAtTime,
        },
      },
    };
  }, [scheduleProcedure, enteredAtTime]);

  return (
    <PanelContainer
      title={<TitleWithIndicator title={title} roomId={roomId} compactView={compactView} />}
      fullOpacity={true}
      timeoutTime={timeoutTime}
      procedureStartTime={procedureStartTime}
      closingTime={closingTime}
      statusLabel={statusLabel}
      timeEntered={formatTimeEntered(enteredAtTime)}
      enteredTime={enteredAtTime}
      compactView={compactView && (!preOpFocused || !pacuFocused)}
      miniView={miniView}
      roomId={roomId}
      status={status}
      numberOfPanels={numberOfPanels}
      scheduleProcedure={derivedScheduleProcedure}
      preOpFocused={preOpFocused}
      pacuFocused={pacuFocused}
      isHelp={isHelp}
      isFlipRoom={isFlipRoom}
      flipRoomTimestamp={flipRoomTimestamp}
      room={operatingRoom}
      messagingView={messagingView}
      hideTurnoverClock={hideTurnoverClock}
      patientId={patientId}
      patientData={patient}
      showScheduledProcedureDuration={showScheduledProcedureDuration}
      roomStaff={roomStaff}
      humanCompact={humanCompact}
    >
      {staffMode ? (
        <StaffShifts
          isFooter={true}
          staffShifts={mockStaff}
          isKiosk={true}
          editableStaff={false}
          type="RoomShift"
          staffMode={staffMode}
        />
      ) : (
        <>
          <AnimatedVisualisation compactView={compactView} $humanCompact={humanCompact} key={status}>
            {!messagingView &&
            (status === 'Cleaning' || (status === 'Vacant' && operatingRoom?.turnoverStartedAt)) &&
            !hideTurnoverClock &&
            !!operatingRoom?.cleaningStartedAt &&
            isSameDate(new Date(operatingRoom?.cleaningStartedAt), new Date()) ? (
              <TurnoverClockComponent
                room={operatingRoom}
                status={status}
                warningThreshold={warningThreshold || undefined}
                alertThreshold={alertThreshold || undefined}
                miniView={miniView}
                humanCompact={humanCompact}
              />
            ) : (
              !messagingView && (
                <Icon
                  src={MONITOR_STATUS_ICONS[status]}
                  compactView={compactView}
                  miniView={miniView}
                  $humanCompact={humanCompact}
                />
              )
            )}
          </AnimatedVisualisation>
          <Footer compactView={compactView}>
            <PatientInfoWrapper
              preOpFocused={!!preOpFocused}
              pacuFocused={!!pacuFocused}
              miniView={miniView}
              messagingView={messagingView}
            >
              <PatientInfo
                status={monitorStatus}
                physician={physician}
                patient={patientName}
                patientId={patientId}
                patientType={patientType}
                procedure={procedure}
                timeEntered={enteredAtTime}
                compactView={compactView}
                statusLabel={statusLabel}
                readyInOr={status === STATUSES.READY}
                bed={patientBedNumber}
                showBedNumber={true}
                allowWrap={allowWrap}
                miniView={miniView}
                preOpFocused={preOpFocused}
                pacuFocused={pacuFocused}
                hideChart={true}
                called={called}
                transferredToChair={transferredToChair}
                transferredToPT={transferredToPT}
                ptComplete={ptComplete}
                isPreOp={isPreOp}
                isSuperAdmin={isSuperAdmin}
                messagingView={messagingView}
                patientData={patient}
                room={operatingRoom}
                isGroupAdmin={isGroupAdmin}
              />
              {messagingView && (
                <Typography
                  style={{ overflow: 'hidden', maxHeight: '2ch', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {procedure}
                </Typography>
              )}
            </PatientInfoWrapper>
          </Footer>
        </>
      )}

      {compactView && !staffMode && (!preOpFocused || !pacuFocused) && (
        <TimesCompact>
          <Entry>
            <Label>Entered</Label>
            <Time>
              {showValueIf(optionalFormat(enteredAtTime, 'HH:mm'), isOrTimeActive(status, OR_TIMES_S.ENTERED_AT))}
            </Time>
          </Entry>
          <Entry>
            <Label>Time Out</Label>
            <Time>
              {showValueIf(optionalFormat(timeoutTime, 'HH:mm'), isOrTimeActive(status, OR_TIMES_S.TIMEOUT_START))}
            </Time>
          </Entry>
          <Entry>
            <Label>Procedure</Label>
            <Time>
              {showValueIf(
                optionalFormat(procedureStartTime, 'HH:mm'),
                isOrTimeActive(status, OR_TIMES_S.PROCEDURE_START)
              )}
            </Time>
          </Entry>
          <Entry>
            <Label>Closing</Label>
            <Time>
              {showValueIf(optionalFormat(closingTime, 'HH:mm'), isOrTimeActive(status, OR_TIMES_S.CLOSING_START))}
            </Time>
          </Entry>
        </TimesCompact>
      )}
    </PanelContainer>
  );
};

export default Panel;
